import { useState } from "react";
import { Aviso, Boton, Etiqueta, Modal, Tabla } from "../../components/ui/primitivos";
import {
  actualizarTamano,
  agregarBase,
  agregarTamano,
  type CatalogoItem,
  fetchPrecios,
  type PrecioListado,
  type Producto,
  quitarBase,
  quitarTamano,
} from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";
import { HistorialPrecios, TONO_VIGENCIA } from "../precios/HistorialPrecios";
import { precioDeTarifa } from "./producto";

/**
 * Las tres secciones del detalle de un producto — porteadas de
 * `TamanosSection`, `BasesSection` y `PreciosSection` de `imprenta`.
 *
 * Son las tres pestañas de `/productos/:id` (ADR 0065), igual que allá. Hasta
 * el 2026-08-22 eran tres secciones apiladas en un modal ancho, y las de abajo
 * quedaban fuera de la vista sin nada que insinuara que estaban.
 */

type Detalle = {
  tamanos: {
    id: string;
    tipoTamanoLogoId: string;
    nombre: string;
    anchoMm: string | null;
    altoMm: string | null;
  }[];
  bases: { id: string; tipoBaseId: string; nombre: string }[];
};

export function SeccionTamanos({
  producto,
  detalle,
  catalogo,
  puedeEditar,
  accion,
}: {
  producto: Producto;
  detalle: Detalle | null;
  catalogo: readonly CatalogoItem[];
  puedeEditar: boolean;
  accion: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [nuevo, setNuevo] = useState({ tipo: "", ancho: "", alto: "" });
  const [editando, setEditando] = useState<Detalle["tamanos"][number] | null>(null);

  const asignados = new Set((detalle?.tamanos ?? []).map((t) => t.tipoTamanoLogoId));

  return (
    <section className="space-y-2">
      <Tabla cabeceras={["Tamaño", "Ancho (mm)", "Alto (mm)", ""]}>
        {(detalle?.tamanos ?? []).map((t) => (
          <tr key={t.id}>
            <td className="px-4 py-2">{t.nombre}</td>
            <td className="px-4 py-2 text-neutral-600">{t.anchoMm ?? "—"}</td>
            <td className="px-4 py-2 text-neutral-600">{t.altoMm ?? "—"}</td>
            <td className="px-4 py-2 text-right">
              {puedeEditar ? (
                <>
                  {/* Editar dimensiones sin quitar y volver a agregar: las
                      medidas son del PRODUCTO (ADR 0010) y se corrigen solas.
                      Quitar borraría los precios que cuelgan del tamaño. */}
                  <Boton variante="fantasma" onClick={() => setEditando(t)}>
                    Medidas
                  </Boton>
                  <Boton
                    variante="peligro"
                    onClick={() => accion(() => quitarTamano(producto.id, t.id))}
                  >
                    Quitar
                  </Boton>
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </Tabla>

      {puedeEditar ? (
        <div className="flex flex-wrap items-end gap-2">
          <select
            aria-label="Tamaño a agregar"
            value={nuevo.tipo}
            onChange={(e) => setNuevo((v) => ({ ...v, tipo: e.target.value }))}
            className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">— elegir tamaño —</option>
            {catalogo
              .filter((t) => !asignados.has(t.id))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
          </select>
          <input
            aria-label="Ancho en mm"
            type="number"
            placeholder="Ancho"
            value={nuevo.ancho}
            onChange={(e) => setNuevo((v) => ({ ...v, ancho: e.target.value }))}
            className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          />
          <input
            aria-label="Alto en mm"
            type="number"
            placeholder="Alto"
            value={nuevo.alto}
            onChange={(e) => setNuevo((v) => ({ ...v, alto: e.target.value }))}
            className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          />
          <Boton
            disabled={!nuevo.tipo}
            onClick={() =>
              accion(async () => {
                await agregarTamano(producto.id, {
                  tipoTamanoLogoId: nuevo.tipo,
                  anchoMm: nuevo.ancho || null,
                  altoMm: nuevo.alto || null,
                });
                setNuevo({ tipo: "", ancho: "", alto: "" });
              })
            }
          >
            Agregar
          </Boton>
        </div>
      ) : null}

      {editando ? (
        <ModalMedidas
          productoId={producto.id}
          tamano={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={async (datos) => {
            await accion(() => actualizarTamano(producto.id, editando.id, datos));
            setEditando(null);
          }}
        />
      ) : null}
    </section>
  );
}

function ModalMedidas({
  productoId: _productoId,
  tamano,
  onCerrar,
  onGuardado,
}: {
  productoId: string;
  tamano: { nombre: string; anchoMm: string | null; altoMm: string | null };
  onCerrar: () => void;
  onGuardado: (datos: { anchoMm: string | null; altoMm: string | null }) => Promise<void>;
}) {
  const [ancho, setAncho] = useState(tamano.anchoMm ?? "");
  const [alto, setAlto] = useState(tamano.altoMm ?? "");

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={`Medidas de ${tamano.nombre}`}
      descripcion="En milímetros, y son de este producto: un Mediano mide distinto en una taza que en una remera"
    >
      <div className="flex gap-3">
        <label className="text-neutral-600 text-sm">
          <span className="block">Ancho (mm)</span>
          <input
            type="number"
            value={ancho}
            onChange={(e) => setAncho(e.target.value)}
            className="mt-1 w-28 rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          />
        </label>
        <label className="text-neutral-600 text-sm">
          <span className="block">Alto (mm)</span>
          <input
            type="number"
            value={alto}
            onChange={(e) => setAlto(e.target.value)}
            className="mt-1 w-28 rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton onClick={() => onGuardado({ anchoMm: ancho || null, altoMm: alto || null })}>
          Guardar
        </Boton>
      </div>
    </Modal>
  );
}

export function SeccionBases({
  producto,
  detalle,
  catalogo,
  puedeEditar,
  accion,
}: {
  producto: Producto;
  detalle: Detalle | null;
  catalogo: readonly CatalogoItem[];
  puedeEditar: boolean;
  accion: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [nueva, setNueva] = useState("");
  const asignadas = new Set((detalle?.bases ?? []).map((b) => b.tipoBaseId));

  return (
    <section className="space-y-2">
      <Tabla cabeceras={["Base", ""]}>
        {(detalle?.bases ?? []).map((b) => (
          <tr key={b.id}>
            <td className="px-4 py-2">{b.nombre}</td>
            <td className="px-4 py-2 text-right">
              {puedeEditar ? (
                <Boton
                  variante="peligro"
                  onClick={() => accion(() => quitarBase(producto.id, b.id))}
                >
                  Quitar
                </Boton>
              ) : null}
            </td>
          </tr>
        ))}
      </Tabla>

      {puedeEditar ? (
        <div className="flex items-end gap-2">
          <select
            aria-label="Base a agregar"
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">— elegir base —</option>
            {catalogo
              .filter((b) => !asignadas.has(b.id))
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nombre}
                </option>
              ))}
          </select>
          <Boton
            disabled={!nueva}
            onClick={() =>
              accion(async () => {
                await agregarBase(producto.id, nueva);
                setNueva("");
              })
            }
          >
            Agregar
          </Boton>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Los precios del producto, con su vigencia.
 *
 * **Muestra los tres estados** —vigente, futuro y expirado— y no solo lo que se
 * cobra hoy: un precio futuro ya cargado es la respuesta a "¿por qué el mes que
 * viene sale distinto?", y uno expirado explica un presupuesto viejo. Es la
 * razón de ser de la vigencia temporal (ADR 0012).
 *
 * El historial de UNA combinación se abre desde la fila, y sale del mismo
 * endpoint acotado con `tamanoId` y `baseId`.
 */
export function SeccionPrecios({ producto }: { producto: Producto }) {
  const [verTodos, setVerTodos] = useState(false);
  const precios = useDatos(() => fetchPrecios(producto.id), producto.id);
  const [historial, setHistorial] = useState<PrecioListado | null>(null);

  const todos = precios.datos?.precios ?? [];
  const visibles = verTodos ? todos : todos.filter((p) => p.estado !== "expirado");
  const resumen = precios.datos?.resumen;

  return (
    <section className="space-y-2">
      {precios.error ? <Aviso>{precios.error}</Aviso> : null}

      {resumen ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-neutral-600">
            {resumen.vigentes} vigente(s) · {resumen.futuros} futuro(s) · {resumen.expirados}{" "}
            expirado(s)
          </span>
          {resumen.expirados > 0 ? (
            <Boton variante="fantasma" onClick={() => setVerTodos((v) => !v)}>
              {verTodos ? "Ocultar expirados" : "Ver expirados"}
            </Boton>
          ) : null}
        </div>
      ) : null}

      {precios.cargando ? (
        <p className="py-4 text-center text-neutral-500 text-sm">Cargando...</p>
      ) : visibles.length === 0 ? (
        <p className="py-4 text-center text-neutral-500 text-sm">
          Sin precios cargados. Hasta que los tenga, este producto no se puede cotizar.
        </p>
      ) : (
        <Tabla cabeceras={["Tamaño", "Base", "Tramo", "Precio", "VIP", "Vigencia", "Estado", ""]}>
          {visibles.map((p) => (
            <tr key={p.id}>
              <td className="px-4 py-2">{p.tamano}</td>
              <td className="px-4 py-2">{p.base}</td>
              <td className="px-4 py-2 text-neutral-600 tabular-nums">
                {p.cantidadDesde}–{p.cantidadHasta ?? "∞"}
              </td>
              <td className="px-4 py-2 tabular-nums">
                {precioDeTarifa(p.precio)}
                {/* Un importe fijo NO es por unidad: sin decirlo, un tramo de
                    100 unidades a 2400 se lee como 240.000. */}
                {p.esImporteFijo ? (
                  <span className="ml-1 text-neutral-500 text-xs">fijo</span>
                ) : null}
              </td>
              <td className="px-4 py-2 text-neutral-600 tabular-nums">
                {precioDeTarifa(p.precioVip)}
              </td>
              <td className="px-4 py-2 text-neutral-600 text-xs">
                {p.vigenteDesde} → {p.vigenteHasta ?? "sin fin"}
              </td>
              <td className="px-4 py-2">
                <Etiqueta tono={TONO_VIGENCIA[p.estado]}>{p.estado}</Etiqueta>
              </td>
              <td className="px-4 py-2 text-right">
                <Boton variante="fantasma" onClick={() => setHistorial(p)}>
                  Historial
                </Boton>
              </td>
            </tr>
          ))}
        </Tabla>
      )}

      {historial ? (
        <HistorialPrecios precio={historial} onCerrar={() => setHistorial(null)} />
      ) : null}
    </section>
  );
}
